import { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  CONTENT_KEY_BIG_FIVE,
  DISC_TEST_ASSESSMENT_ID,
  ENNEAGRAM_TEST_ASSESSMENT_ID,
  MODEL_TYPE_SOCIAL_ANXIETY,
  PERSONALITY_TEST_ASSESSMENT_ID,
} from "../../utils/general-config";
import {
  getSocialAnxietyReportPageImage,
  getEnneagramBannerImage,
} from "../../utils/utility-functions";
import Header from "../../components/header";
import BackToTopButton from "../../components/back-to-top/component";
import Loader from "../../components/loader";
import Footer from "../../components/footer";
import ShareButton from "../../components/share-button";
import SendToFriendButton from "../../components/send-to-friend-button";
import ExpandableSection from "../../components/expandable-collapsible/component";
import ExternalLink from "../../assets/settings/external-link-icon.svg";
import {
  APP_NAVIGATION,
  ASSESSMENT_BACKGROUND_COLORS,
  LOCALE,
} from "../../utils/constants";
import { useTranslation } from "react-i18next";
import InviteFriend from "../../components/invite-friend";
import { CallToActionSection } from "../../components/call-to-action/component";
import PathHelper from "../../utils/path-helper";
import {
  FACTOR_SEVERITY_RANGES,
  getLevelByRanges,
  getSocialAnxietyOverallPercentage,
  OVERALL_SEVERITY_RANGES,
  SOCIAL_ANXIETY_TRAITS,
} from "../../utils/assessment-utils";
import ReportWrapper from "../../components/report-wrapper";

const SocialAnxietyReport = ({
  getAssessmentResultByAssessmentAttemptIdConnect,
  getInsightfulContentConnect,
  getUserByIdConnect,
  sendToFriendReportOnEmailConnect,
  funnelStep,
  getAssessmentConnect,
  getAssessmentAttemptByUserIdConnect,
  getGuestUserConnect,
  getOrderDataByUserIdConnect,
  getBillingConnect,
  guestUserChangesConnect,
  isGuestOrPremiumUserConnect,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [contentData, setContentData] = useState({});
  const [userTraits, setUserTraits] = useState([]);
  const location = useLocation();

  const queryParams = new URLSearchParams(location.search);
  const assessmentAttemptId = queryParams.get("aai");
  const [isLoading, setIsLoading] = useState(false);
  const [currentAssessmentAttemptData, setCurrentAssessmentAttemptData] =
    useState("");
  const { userGUID, isAuth, selectedPlan } = funnelStep || {};
  const [userData, setUserData] = useState(null);
  // const token = getCookie("jwtToken");
  const userId = queryParams.get("uid") || userGUID;
  const { locale } = useParams();
  const currentLocale = locale ?? LOCALE.EN;
  const pathSegments = location.pathname.split("/");
  const reportType = pathSegments[4];
  const assessmentId = currentAssessmentAttemptData?.assessmentId;
  const quizPath = PathHelper(APP_NAVIGATION.QUIZ, currentLocale);

  useEffect(() => {
    if (!assessmentAttemptId) return;

    const fetchAssessmentAndContent = async () => {
      setIsLoading(true); // Start loading
      try {
        // Fetch user traits
        const traitsResponse =
          await getAssessmentResultByAssessmentAttemptIdConnect(
            assessmentAttemptId,
            currentLocale,
          );
        if (traitsResponse) {
          setUserTraits(traitsResponse.userTraits);
          setCurrentAssessmentAttemptData(traitsResponse);
        }

        // Fetch content
        const contentResponse = await getInsightfulContentConnect({
          modelType:
            MODEL_TYPE_SOCIAL_ANXIETY[currentLocale] ||
            MODEL_TYPE_SOCIAL_ANXIETY[LOCALE.EN],

          contentKey: CONTENT_KEY_BIG_FIVE,
        });
        if (contentResponse) {
          const { content } = contentResponse;
          setContentData(content);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setIsLoading(false); // End loading
      }
    };

    fetchAssessmentAndContent();
  }, [assessmentAttemptId]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [isLoading]);

  useEffect(() => {
    if (userId && isAuth) {
      getUserByIdConnect(userId).then((res) => {
        if (res) {
          setUserData(res);
        }
      });
    }
  }, [userId, isAuth]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [isLoading]);

  const colorVariants = {
    purple: "from-purple-600 via-purple-500",
    teal: "from-teal-600 via-teal-500",
    blue: "from-blue-600 via-blue-500",
    red: "from-red-600 via-red-500",
    green: "from-green-600 via-green-500",
    yellow: "from-yellow-600 via-yellow-500",
    indigo: "from-indigo-600 via-indigo-500",
  };

  const bannerImage = getEnneagramBannerImage();
  const result1ImageText = "sat-result-1";
  const result1Image = getSocialAnxietyReportPageImage(result1ImageText);
  const result2ImageText = "sat-result-2";
  const result2Image = getSocialAnxietyReportPageImage(result2ImageText);
  const result3ImageText = "sat-result-3";
  const result3Image = getSocialAnxietyReportPageImage(result3ImageText);
  const result4ImageText = "sat-result-4";
  const result4Image = getSocialAnxietyReportPageImage(result4ImageText);
  const result5ImageText = "sat-result-5";
  const result5Image = getSocialAnxietyReportPageImage(result5ImageText);

  const overallScore = getSocialAnxietyOverallPercentage(userTraits);
  const overallLevel = getLevelByRanges(overallScore, OVERALL_SEVERITY_RANGES);
  const traitMap = Object.fromEntries(
    userTraits.map((trait) => [trait.traitId.toLowerCase(), trait]),
  );

  const socialAnxietyTraitsData = {
    attentionPerformance:
      traitMap[SOCIAL_ANXIETY_TRAITS.PERFORMANCE_ATTENTION.toLowerCase()],
    authorityFear: traitMap[SOCIAL_ANXIETY_TRAITS.AUTHORITY_FEAR.toLowerCase()],
    physicalSymptoms:
      traitMap[SOCIAL_ANXIETY_TRAITS.PHYSICAL_SYMPTOMS.toLowerCase()],
    criticismEmbarrassment:
      traitMap[SOCIAL_ANXIETY_TRAITS.CRITICISM_EMBARRASSMENT.toLowerCase()],
    socialInteraction:
      traitMap[SOCIAL_ANXIETY_TRAITS.SOCIAL_INTERACTION.toLowerCase()],
  };
  const socialAnxietyTraitLabels = {
    authorityFear: getLevelByRanges(
      socialAnxietyTraitsData.authorityFear?.score ?? 0,
      FACTOR_SEVERITY_RANGES,
    ),

    socialInteraction: getLevelByRanges(
      socialAnxietyTraitsData.socialInteraction?.score ?? 0,
      FACTOR_SEVERITY_RANGES,
    ),

    attentionPerformance: getLevelByRanges(
      socialAnxietyTraitsData.attentionPerformance?.score ?? 0,
      FACTOR_SEVERITY_RANGES,
    ),

    criticismEmbarrassment: getLevelByRanges(
      socialAnxietyTraitsData.criticismEmbarrassment?.score ?? 0,
      FACTOR_SEVERITY_RANGES,
    ),

    physicalSymptoms: getLevelByRanges(
      socialAnxietyTraitsData.physicalSymptoms?.score ?? 0,
      FACTOR_SEVERITY_RANGES,
    ),
  };
  const factorsScores = userTraits.map((item) => {
    return {
      level: getLevelByRanges(item?.score ?? 0, FACTOR_SEVERITY_RANGES),
      title: item.trait.title,
      percentage: item.percentage,
    };
  });

  const highestTrait = [...userTraits].sort(
    (a, b) => (b.percentage ?? 0) - (a.percentage ?? 0),
  )[0];

  const topFactorContentMap = {
    [SOCIAL_ANXIETY_TRAITS.SOCIAL_INTERACTION.toLowerCase()]:
      contentData?.topFactors?.factors?.socialInteraction,

    [SOCIAL_ANXIETY_TRAITS.CRITICISM_EMBARRASSMENT.toLowerCase()]:
      contentData?.topFactors?.factors?.criticismEmbarrassment,

    [SOCIAL_ANXIETY_TRAITS.PHYSICAL_SYMPTOMS.toLowerCase()]:
      contentData?.topFactors?.factors?.physicalSymptoms,

    [SOCIAL_ANXIETY_TRAITS.AUTHORITY_FEAR.toLowerCase()]:
      contentData?.topFactors?.factors?.authorityFear,

    [SOCIAL_ANXIETY_TRAITS.PERFORMANCE_ATTENTION.toLowerCase()]:
      contentData?.topFactors?.factors?.attentionPerformance,
  };

  const topFactor = topFactorContentMap[highestTrait?.traitId?.toLowerCase()];

  const content = contentData?.howScored?.content || "";

  const lines = content
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean);

  const intro = lines[0];

  const factorItems = lines.slice(1, 6);

  const remainingText = lines.slice(6);

  const TakeTestButton = ({ onClick, id }) => {
    return (
      <button
        className="flex items-center gap-2 px-6 py-3 bg-white text-black font-semibold rounded-full shadow-md border border-gray-300 hover:bg-gray-100 transition-all"
        onClick={onClick}
        id={id}
      >
        {t("report.adhd.take_test_cta")}
        <svg
          fill="black"
          width="20"
          height="20"
          viewBox="-2 -2 24 24"
          xmlns="http://www.w3.org/2000/svg"
          preserveAspectRatio="xMinYMin"
          className="jam jam-arrow-circle-right"
        >
          <path d="M10 20C4.477 20 0 15.523 0 10S4.477 0 10 0s10 4.477 10 10-4.477 10-10 10zm0-2a8 8 0 1 0 0-16 8 8 0 0 0 0 16zm1.586-7H6a1 1 0 0 1 0-2h5.586L9.05 6.464a1 1 0 1 1 1.414-1.414l4.243 4.243a.997.997 0 0 1 0 1.414l-4.243 4.243a1 1 0 1 1-1.414-1.414L11.586 11z" />
        </svg>
      </button>
    );
  };

  const HeroBanner = ({ title, color = "purple", buttonNeeded, onClick }) => {
    const gradientClasses = colorVariants[color] || colorVariants.purple;

    return (
      <div
        className={`relative h-auto bg-gradient-to-r ${gradientClasses} to-purple-600`}
      >
        {/* Background Image (Hidden on small screens) */}
        <img
          src={bannerImage}
          alt={title}
          className="absolute bottom-0 right-0 w-full md:w-2/3 h-full object-cover object-right"
        />

        {/* Gradient Overlay */}
        <div
          className={`block absolute inset-0 bg-gradient-to-r ${gradientClasses} to-transparent`}
        />
        <div className="absolute inset-0 bg-black opacity-10" />

        {/* Hero Content */}
        <div className="relative h-full flex items-center" id={reportType}>
          <div className="w-full px-4 py-8 lg:px-8 lg:py-12" id={reportType}>
            {/* Title & Button Wrapper */}
            <div
              className="flex flex-col md:flex-row items-start justify-between gap-4 pr-10 md:pr-16"
              id={reportType}
            >
              {/* Title */}
              <div className="flex-1 min-w-0 w-full">
                <h2
                  id={reportType}
                  style={{ textShadow: "2px 2px 4px rgba(0, 0, 0, 0.5)" }}
                  className="w-full text-left text-3xl md:text-4xl font-bold text-white break-words whitespace-pre-wrap"
                >
                  {title}
                </h2>
              </div>

              {/* Button (if needed) */}
              {buttonNeeded && (
                <div className="flex-shrink-0">
                  <TakeTestButton onClick={onClick} />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const InfoCard = ({ heading, items, bgClr }) => {
    return (
      <div
        className={`bg-${bgClr}-50 p-4 md:p-6 rounded-lg shadow-md border border-green-50 w-full h-full flex flex-col`}
      >
        <h2 className="font-bold text-lg text-gray-900">{heading}</h2>
        <ul className="list-disc list-outside pl-5 text-gray-700 mt-4">
          {items.map((item, index) => (
            <li key={index} className="break-words">
              {item}
            </li>
          ))}
        </ul>
      </div>
    );
  };

  const CallToActionSectionForUnAuth = ({ data }) => {
    return (
      <section className="space-y-6">
        <div className="block bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
          <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-8 justify-center items-center flex flex-col gap-2">
            <h3 className="text-xl font-bold text-white text-center">
              {t("report.personality.personality_test_invitation")}
            </h3>
            <p className="text-white opacity-90 text-center">
              {t("report.personality.personality_type_learn_more")}
            </p>
            <button
              className="bg-white text-indigo-600 px-6 py-2 rounded-full font-semibold hover:bg-indigo-100 transition-colors w-fit"
              id={"likeability-report-take-test-cta"}
              onClick={() => {
                navigate(
                  `${quizPath.next}?assessmentId=${PERSONALITY_TEST_ASSESSMENT_ID}&newt=true`,
                );
              }}
            >
              {t("welcomeModal.take_the_test")}
            </button>
          </div>
        </div>
        <div className="block bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
          <div className="bg-gradient-to-r from-purple-600 to-pink-600 p-8 flex flex-col justify-center items-center gap-2">
            <h3 className="text-xl font-bold text-white text-center">
              {t("report.enneagram.enneagram_test_invitation")}
            </h3>
            <p className="text-white opacity-90 text-center">
              {t("report.enneagram.enneagram_self_awareness")}
            </p>
            <button
              className="bg-white text-indigo-600 px-6 py-2 rounded-full font-semibold hover:bg-indigo-100 transition-colors w-fit"
              id={"likeability-report-take-enneagram-test-cta"}
              onClick={() => {
                navigate(
                  `${quizPath.next}?assessmentId=${ENNEAGRAM_TEST_ASSESSMENT_ID}&newt=true`,
                );
              }}
            >
              {t("welcomeModal.take_the_test")}
            </button>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
          <div className="bg-gradient-to-r from-orange-600 to-yellow-600 p-8 justify-center items-center flex flex-col gap-2">
            <h3 className="text-xl font-bold text-white text-center">
              {t("report.disc_personality.disc_test_invitation")}
            </h3>
            <p className="text-white opacity-90 text-center">
              {t("report.disc_personality.take_assessment_cta")}
            </p>
            <button
              id={"likeability-report-take-disc-test-cta"}
              s
              className="bg-white text-indigo-600 px-6 py-2 rounded-full font-semibold hover:bg-indigo-100 transition-colors w-fit"
              onClick={() => {
                navigate(
                  `${quizPath.next}?assessmentId=${DISC_TEST_ASSESSMENT_ID}&newt=true`,
                );
              }}
            >
              {t("welcomeModal.take_the_test")}
            </button>
          </div>
        </div>
      </section>
    );
  };
  const ContentCard = ({
    heading,
    bgClr,
    contentArray,
    overview,
    character,
    isSubHeading = false,
    level,
    cardIndex = 0,
  }) => {
    const headingColors = [
      "#3B82F6", // Blue
      "#10B981", // Green
      "#F59E0B", // Amber
      "#EF4444", // Red
      "#8B5CF6", // Purple
    ];
    return (
      <div
        style={{ backgroundColor: bgClr }}
        className="p-4 md:p-6 rounded-2xl shadow-lg flex flex-col h-full"
      >
        {heading && (
          <div className="flex items-start gap-2 mb-2">
            <div>
              <div className="flex items-start gap-2">
                {character && (
                  <div className="shrink-0 flex items-center justify-center rounded-full border-4 border-white shadow-[0_0_5px_rgba(0,0,0,1)]">
                    <CircleIcon character={character} size={20} />
                  </div>
                )}

                <h2
                  className="text-lg sm:text-xl font-semibold"
                  style={{
                    color: headingColors[cardIndex % headingColors.length],
                  }}
                >
                  {heading}
                  {level && (
                    <>
                      : <span className="text-[#805bec]">{level}</span>
                    </>
                  )}
                </h2>
              </div>
            </div>
          </div>
        )}

        {overview && (
          <p className={`text-lg text-gray-700 ${contentArray ? `mb-4` : ""}`}>
            {overview}
          </p>
        )}
        {contentArray && (
          <ul className="space-y-2 mt-4">
            {contentArray?.map((item, index) => {
              return (
                <li key={index} className="flex gap-4">
                  {/* Left Section: Circle Icon */}
                  <div className="w-4 h-4 flex items-center justify-center rounded-full border-2 border-white shadow-[0_0_5px_rgba(0,0,0,1)] mt-[5px]">
                    <CircleIcon character={""} size={16} />
                  </div>

                  {/* Right Section: Heading & Content */}
                  <div className="flex flex-col">
                    {isSubHeading ? (
                      <>
                        {/* Heading and First Line in Same Row */}
                        <div className="flex items-center gap-2">
                          <h2 className="text-lg font-semibold text-gray-700">
                            {item?.heading}
                          </h2>
                        </div>

                        {/* Paragraph with text-indent effect */}
                        <p className="text-gray-700 text-base md:text-lg leading-relaxed relative">
                          <span className="">{item.content}</span>
                        </p>
                      </>
                    ) : (
                      <p className="text-gray-700 text-base md:text-lg leading-relaxed break-words">
                        {typeof item === "string" && item.includes(":") ? (
                          <>
                            <span
                              className="font-semibold"
                              style={{
                                color: colors[index % colors.length],
                              }}
                            >
                              {item.split(":")[0]}
                            </span>
                            : {item.split(":").slice(1).join(":").trim()}
                          </>
                        ) : typeof item === "string" ? (
                          item
                        ) : (
                          item?.label
                        )}
                      </p>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    );
  };
  const InfoListCard = ({
    heading,
    bgClr,
    contentArray,
    overview,
    character,
    isSubHeading = false,
    className = "",
    isCard = true,
  }) => {
    return (
      <div
        style={{ backgroundColor: isCard ? bgClr : "transparent" }}
        className={`
  flex flex-col h-full
  ${isCard ? "p-4 md:p-6 rounded-2xl shadow-lg" : ""}
  ${className}
`}
      >
        {heading && (
          <div className="flex items-start gap-2 mb-2">
            <div>
              <div className="flex items-start gap-2">
                {character && (
                  <div className="shrink-0 flex items-center justify-center rounded-full border-4 border-white shadow-[0_0_5px_rgba(0,0,0,1)]">
                    {" "}
                    <CircleIcon character={character} size={20} />
                  </div>
                )}
                <h2 className="text-lg sm:text-xl font-semibold text-gray-800 flex items-center">
                  {heading}
                </h2>
              </div>
            </div>
          </div>
        )}

        {overview && (
          <p className={`text-lg text-gray-700 ${contentArray ? `mb-4` : ""}`}>
            {overview}
          </p>
        )}
        {contentArray && (
          <ul className="space-y-2 mt-4 pl-6 md:pl-10">
            {contentArray?.map((item, index) => {
              return (
                <li key={index} className="flex gap-4">
                  {/* Left Section: Circle Icon */}
                  <div className="w-4 h-4 flex items-center justify-center rounded-full border-2 border-white shadow-[0_0_5px_rgba(0,0,0,1)] mt-[5px]">
                    <CircleIcon character={""} size={16} />
                  </div>

                  {/* Right Section: Heading & Content */}
                  <div className="flex flex-col">
                    {isSubHeading ? (
                      <>
                        {/* Heading and First Line in Same Row */}
                        <div className="flex items-center gap-2">
                          <h2 className="text-lg font-semibold text-gray-700">
                            {item?.heading}
                          </h2>
                        </div>

                        {/* Paragraph with text-indent effect */}
                        <p className="text-gray-700 text-base md:text-lg leading-relaxed relative">
                          <span className="">{item.content}</span>
                        </p>
                      </>
                    ) : (
                      <p className="text-gray-700 text-base md:text-lg leading-relaxed">
                        {item}
                      </p>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    );
  };
  const CircleIcon = ({ character = "1", size = 24, color = "#7D5FFF" }) => {
    const viewBoxSize = 100;
    const centerPoint = viewBoxSize / 2;

    const minSize = 16;
    const actualSize = Math.max(size, minSize);
    const isBullet = character === "";
    const isQuestion = character === "?";
    const isNumber = !isNaN(parseInt(character));
    let finalColor;

    if (color !== "#7D5FFF") {
      finalColor = "#336bcc";
    } else {
      if (isQuestion) {
        finalColor = "#805bec";
      } else if (isNumber) {
        finalColor = "#805bec";
      } else {
        finalColor = "#805bec";
      }
    }

    const textSize = isQuestion ? 60 : 50;

    return (
      <svg
        width={actualSize}
        height={actualSize}
        viewBox={`0 0 ${viewBoxSize} ${viewBoxSize}`}
        className="min-w-[16px] min-h-[16px]"
        style={{ filter: "drop-shadow(0 0 5px rgba(0,0,0,0.3))" }}
      >
        <circle
          cx={centerPoint}
          cy={centerPoint}
          r={isBullet ? 42 : 48}
          fill={finalColor}
          stroke="none"
        />

        <text
          x={centerPoint}
          y={centerPoint + (isQuestion ? 4 : 2)}
          fontSize={textSize}
          fontWeight="bold"
          fill="white"
          textAnchor="middle"
          dominantBaseline="middle"
          fontFamily="Arial, sans-serif"
        >
          {character}
        </text>
      </svg>
    );
  };
  const NextStepsCard = ({
    heading,
    intro,
    bgClr,
    contentArray,
    overview,
    character,
  }) => {
    return (
      <div
        style={{ backgroundColor: bgClr }}
        className="p-4 md:p-6 rounded-2xl shadow-lg flex flex-col h-full"
      >
        {/* HEADING */}
        {heading && (
          <div className="flex items-start gap-2 mb-3">
            {/* {character && (
              <div className="shrink-0 flex items-center justify-center rounded-full border-4 border-white shadow-[0_0_5px_rgba(0,0,0,1)]">
                <CircleIcon character={character} size={20} />
              </div>
            )} */}

            <div>
              <h2 className="text-lg sm:text-xl font-semibold text-gray-800">
                {heading}
              </h2>

              {/* OPTIONAL INTRO */}
              {intro && (
                <p className="text-gray-600 text-sm md:text-base mt-2 leading-relaxed">
                  {intro}
                </p>
              )}
            </div>
          </div>
        )}

        {/* OVERVIEW */}
        {overview && (
          <p className="text-gray-700 text-base md:text-lg mb-4">{overview}</p>
        )}

        {/* CONTENT LIST */}
        {contentArray && (
          <ul className="space-y-4 mt-2">
            {contentArray.map((item, index) => (
              <li key={index} className="flex gap-3 items-start">
                {/* BULLET */}
                <div className="w-4 h-4 flex-shrink-0 flex items-center justify-center rounded-full border-2 border-white shadow-[0_0_5px_rgba(0,0,0,1)] mt-1">
                  <CircleIcon character={""} size={16} />
                </div>

                {/* TEXT / LINK */}
                <div className="flex flex-col">
                  {/* Optional Heading */}
                  {item?.Heading && (
                    <h3 className="text-base font-semibold text-gray-700">
                      {item.Heading}
                    </h3>
                  )}

                  {/* Content (Clickable if link exists) */}
                  {item?.url ? (
                    <a
                      href={item.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-gray-700 text-base md:text-lg leading-relaxed hover:text-blue-600 hover:underline transition break-words"
                    >
                      {item.label}
                      <img
                        src={ExternalLink}
                        alt="External Link"
                        className="inline-block ml-1 w-4 h-4"
                      />
                    </a>
                  ) : (
                    <p className="text-gray-700 text-base md:text-lg leading-relaxed break-words">
                      {item.label}
                    </p>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    );
  };
  const DescriptiveCard = ({
    heading,
    bgClr,
    contentArray,
    overview,
    character,
    isSubHeading = false,
    textClr,
    level,
  }) => {
    return (
      <div
        className={`p-4 md:p-6 rounded-2xl shadow-lg ${bgClr} flex flex-col h-full`}
      >
        {heading && (
          <div className="flex items-start gap-2 mb-2">
            <div>
              <div className="flex items-start gap-2">
                {character && (
                  <div className="shrink-0 flex items-center justify-center rounded-full border-4 border-white shadow-[0_0_5px_rgba(0,0,0,1)]">
                    {" "}
                  </div>
                )}

                <h2
                  className={`text-lg sm:text-xl ${textClr}  font-semibold  flex items-center`}
                >
                  {heading}
                </h2>
              </div>
            </div>
          </div>
        )}
        {level && (
          <div
            className={`text-sm  sm:text-lg font-semibold ${textClr} bg-opacity-30`}
          >
            {t("report.childhood_trauma.level")}: &nbsp;
            {level}
          </div>
        )}

        {overview && (
          <p
            className={`text-lg text-gray-700 mt-4 ${
              character ? "pl-8 md:pl-10" : ""
            } ${contentArray ? `mb-4` : ""}`}
          >
            {overview}
          </p>
        )}
        {contentArray && (
          <ul
            className={`space-y-2 mt-4 ${
              overview ? "pl-8 md:pl-12" : "md:pl-6 pl-2"
            }`}
          >
            {contentArray?.map((item, index) => {
              return (
                <li key={index} className="flex gap-4">
                  {/* Left Section: Circle Icon */}
                  <div className="w-4 h-4 flex items-center justify-center rounded-full border-2 border-white shadow-[0_0_5px_rgba(0,0,0,1)] mt-[5px]"></div>

                  {/* Right Section: Heading & Content */}
                  <div className="flex flex-col">
                    {isSubHeading ? (
                      <>
                        {/* Heading and First Line in Same Row */}
                        <div className="flex items-center gap-2">
                          <h2 className="text-lg font-semibold text-gray-700">
                            {item?.heading}
                          </h2>
                        </div>

                        {/* Paragraph with text-indent effect */}
                        <p className="text-gray-700 text-base md:text-lg leading-relaxed relative">
                          <span className="">{item.content}</span>
                        </p>
                      </>
                    ) : (
                      <p className="text-gray-700 text-base md:text-lg leading-relaxed">
                        {item}
                      </p>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    );
  };

  const colors = ["#EEF4FF", "#EAF7F6", "#FFF4E5", "#FCEEF5", "#F5F1FF"];
  return (
    <>
      <Header />
      <BackToTopButton />
      <div className="min-h-screen">
        <div className="container mx-auto p-4 sm:p-8">
          {isLoading ? (
            <div className="flex justify-center items-center h-screen w-full">
              <Loader isLoading={isLoading} />
            </div>
          ) : (
            <>
              {/* SECTION 1 - Test Results */}
              <section className="bg-white rounded-xl shadow-lg overflow-hidden mb-8">
                <ExpandableSection
                  defaultExpanded={true}
                  heroBanner={
                    <HeroBanner title={contentData?.header} color="blue" />
                  }
                >
                  {isAuth && (
                    <div className="w-full pt-[10px] flex justify-end lg:justify-end gap-[10px] lg:px-6">
                      <ShareButton
                        currentAssessmentAttemptData={
                          currentAssessmentAttemptData
                        }
                        userGUID={userGUID}
                        userData={userData}
                      />
                      <SendToFriendButton
                        currentAssessmentAttemptData={
                          currentAssessmentAttemptData
                        }
                        userGUID={userGUID}
                        userData={userData}
                        CreateGuestUserConnect={
                          sendToFriendReportOnEmailConnect
                        }
                      />
                    </div>
                  )}
                  <div className="w-full p-6 md:px-8 md:py-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-8 items-start">
                      {/* LEFT - ABOUT */}
                      <div className="flex flex-col h-full">
                        <h3 className="text-lg md:text-xl font-semibold text-gray-700">
                          {t("report.social_anxiety.overview")}
                        </h3>
                        <div className="mt-8">
                          <p className="text-base text-gray-700 leading-relaxed">
                            {contentData?.intro}
                          </p>
                        </div>
                      </div>

                      {/* TRAIT SCORE */}
                      <div className="flex flex-col h-full gap-1">
                        <h3 className="text-lg md:text-xl font-semibold text-gray-700">
                          {contentData?.overallResult?.heading}
                        </h3>

                        <h2 className="text-lg md:text-xl font-semibold text-gray-700 whitespace-nowrap">
                          {
                            contentData?.overallResult?.bands?.[overallLevel]
                              ?.label
                          }{" "}
                          {t("report.social_anxiety.social_anxiety")}
                        </h2>

                        {/* Score Section */}
                        <div className="w-full max-w-md ">
                          <h3 className="text-lg md:text-xl font-semibold text-gray-700">
                            {t("report.social_anxiety.score")} {overallScore}
                            /100
                          </h3>

                          <div className="mt-2 w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                            <div
                              className="bg-orange-500 h-full rounded-full"
                              style={{ width: `${overallScore}%` }}
                            />
                          </div>
                        </div>

                        <div className="mt-8">
                          <p className="text-base text-gray-700 leading-relaxed">
                            {
                              contentData?.overallResult?.bands?.[overallLevel]
                                ?.description
                            }
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </ExpandableSection>
              </section>

              {/* SECTION 2 - How is this Scored */}
              <section className="rounded-xl shadow-lg overflow-hidden mb-8">
                <ExpandableSection
                  heroBanner={
                    <HeroBanner
                      title={contentData?.howScored?.heading}
                      color="teal"
                    />
                  }
                >
                  <div className="w-full p-4 md:px-8 md:py-6">
                    {/* INTRO */}
                    <p className="text-gray-700 text-base md:text-lg leading-relaxed mb-6 font-bold">
                      {intro}
                    </p>

                    {/* First Row */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                      {factorItems?.slice(0, 3).map((item, index) => {
                        const [heading, ...rest] = item.split(":");

                        return (
                          <ContentCard
                            key={index}
                            cardIndex={index}
                            bgClr={colors[index % colors.length]}
                            heading={heading}
                            overview={rest.join(":").trim()}
                          />
                        );
                      })}
                    </div>

                    {/* Second Row */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-8">
                      {factorItems?.slice(3).map((item, index) => {
                        const [heading, ...rest] = item.split(":");

                        return (
                          <ContentCard
                            key={index + 3}
                            cardIndex={index + 3}
                            bgClr={colors[(index + 3) % colors.length]}
                            heading={heading}
                            overview={rest.join(":").trim()}
                          />
                        );
                      })}
                    </div>

                    {/* TEXT + IMAGE */}
                    <div className="grid grid-cols-1 lg:grid-cols-[60%_40%] gap-8 items-center mt-8">
                      <div>
                        {remainingText.map((text, index) => (
                          <p
                            key={index}
                            className="text-gray-700 text-base md:text-lg leading-relaxed mb-4"
                          >
                            {text}
                          </p>
                        ))}
                      </div>

                      <div className="flex justify-center">
                        <img
                          src={result1Image}
                          alt="Banner Image"
                          className="w-full max-w-full object-contain"
                        />
                      </div>
                    </div>
                  </div>
                </ExpandableSection>
              </section>

              {/* SECTION 3 - UNDERSTANDING RESULTS */}
              <section className="rounded-xl shadow-lg overflow-hidden mb-8">
                <ExpandableSection
                  heroBanner={
                    <HeroBanner
                      title={`${contentData?.understandingResults?.heading} (${overallScore}/100)`}
                      color="red"
                    />
                  }
                >
                  <div className="w-full p-4 md:px-8 md:py-6">
                    <div className="px-4 w-full max-w-full overflow-hidden">
                      <div
                        className="whitespace-pre-line text-gray-700 text-base md:text-lg leading-relaxed"
                        dangerouslySetInnerHTML={{
                          __html:
                            contentData?.understandingResults?.content || "",
                        }}
                      />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-8">
                      <div className="w-full">
                        <DescriptiveCard
                          heading={`${contentData?.factors?.socialInteraction.label}: ${socialAnxietyTraitsData?.socialInteraction?.percentage ?? 0}/100`}
                          textClr="text-orange-800"
                          overview={
                            contentData?.factors?.socialInteraction.bands[
                              socialAnxietyTraitLabels.socialInteraction
                            ]?.description
                          }
                          level={
                            contentData?.factors?.socialInteraction.bands[
                              socialAnxietyTraitLabels.socialInteraction
                            ]?.label
                          }
                          bgClr={"bg-orange-50"}
                        />
                      </div>

                      <div className="w-full">
                        <DescriptiveCard
                          heading={`${contentData?.factors?.criticismEmbarrassment.label}: ${socialAnxietyTraitsData?.criticismEmbarrassment?.percentage ?? 0}/100`}
                          textClr="text-yellow-800"
                          overview={
                            contentData?.factors?.criticismEmbarrassment.bands[
                              socialAnxietyTraitLabels.criticismEmbarrassment
                            ]?.description
                          }
                          level={
                            contentData?.factors?.criticismEmbarrassment.bands[
                              socialAnxietyTraitLabels.criticismEmbarrassment
                            ]?.label
                          }
                          bgClr={"bg-yellow-50"}
                        />
                      </div>

                      <div className="w-full">
                        <DescriptiveCard
                          heading={`${contentData?.factors?.physicalSymptoms.label}: ${socialAnxietyTraitsData?.physicalSymptoms?.percentage ?? 0}/100`}
                          textClr="text-yellow-800"
                          overview={
                            contentData?.factors?.physicalSymptoms.bands[
                              socialAnxietyTraitLabels.physicalSymptoms
                            ]?.description
                          }
                          level={
                            contentData?.factors?.physicalSymptoms.bands[
                              socialAnxietyTraitLabels.physicalSymptoms
                            ]?.label
                          }
                          bgClr={"bg-yellow-50"}
                        />
                      </div>

                      <div className="w-full">
                        <DescriptiveCard
                          heading={`${contentData?.factors?.authorityFear.label}: ${socialAnxietyTraitsData?.authorityFear?.percentage ?? 0}/100`}
                          textClr="text-orange-800"
                          overview={
                            contentData?.factors?.authorityFear.bands[
                              socialAnxietyTraitLabels.authorityFear
                            ]?.description
                          }
                          level={
                            contentData?.factors?.authorityFear.bands[
                              socialAnxietyTraitLabels.authorityFear
                            ]?.label
                          }
                          bgClr={"bg-orange-50"}
                        />
                      </div>

                      <div className="w-full">
                        <DescriptiveCard
                          heading={`
                          ${contentData?.factors?.attentionPerformance.label} : ${socialAnxietyTraitsData?.attentionPerformance?.percentage ?? 0}/100 `}
                          textClr="text-pink-800"
                          overview={
                            contentData?.factors?.attentionPerformance.bands[
                              socialAnxietyTraitLabels.attentionPerformance
                            ]?.description
                          }
                          level={
                            contentData?.factors?.attentionPerformance.bands[
                              socialAnxietyTraitLabels.attentionPerformance
                            ]?.label
                          }
                          bgClr={"bg-pink-50"}
                        />
                      </div>
                      <div className="flex items-center justify-center min-h-full">
                        <img
                          src={result2Image}
                          alt="Banner Image"
                          className="w-full max-w-full h-auto object-contain mx-auto"
                        />
                      </div>
                    </div>
                  </div>
                </ExpandableSection>
              </section>

              {/* SECTION 4 - Your Anxiety Profile */}
              <section className="rounded-xl shadow-lg overflow-hidden mb-8">
                <ExpandableSection
                  heroBanner={
                    <HeroBanner
                      title={contentData?.topFactors?.heading}
                      color="green"
                    />
                  }
                >
                  <div className="w-full p-4 md:px-8 md:py-6">
                    {topFactor && (
                      <InfoListCard
                        heading={""}
                        overview={topFactor?.profile}
                        character="i"
                        contentArray={topFactor?.triggers}
                        isCard={false}
                      />
                    )}
                  </div>
                </ExpandableSection>
              </section>

              {/* SECTION 5 - Tips for Managing Social Anxiety */}
              <section className="rounded-xl shadow-lg overflow-hidden mb-8">
                <ExpandableSection
                  heroBanner={
                    <HeroBanner
                      title={contentData?.tips?.heading}
                      color="indigo"
                    />
                  }
                >
                  <div className="w-full p-4 md:px-8 md:py-6">
                    <div className="grid grid-cols-1 lg:grid-cols-[60%_40%] gap-4 items-start">
                      <div className="w-full">
                        <NextStepsCard
                          heading=""
                          intro={contentData?.tips?.intro}
                          character="i"
                          bgClr="#eff4fd"
                          contentArray={contentData?.tips?.items?.map(
                            (item) => ({
                              label: item,
                            }),
                          )}
                        />
                      </div>
                      <div className="flex items-center justify-center h-full">
                        <img
                          src={result3Image}
                          alt="Banner Image"
                          className="w-full max-w-full mx-auto object-contain "
                        />
                      </div>
                    </div>
                  </div>
                </ExpandableSection>
              </section>

              {/* SECTION 6 - When to Seek Help */}
              <section className="rounded-xl shadow-lg overflow-hidden mb-8">
                <ExpandableSection
                  heroBanner={
                    <HeroBanner
                      title={contentData?.seekHelp?.heading}
                      color="yellow"
                    />
                  }
                >
                  <div className="w-full p-4 md:px-8 md:py-6">
                    <div className="px-4 text-gray-700 text-base md:text-lg leading-relaxed">
                      {/* Image */}
                      <img
                        src={result4Image}
                        alt="Seek Professional Help"
                        className="w-full max-w-full md:max-w-md h-auto object-contain float-none lg:float-right lg:ml-8 mb-6"
                      />

                      {/* Text */}
                      <p className="whitespace-pre-line">
                        {contentData?.seekHelp?.base}
                      </p>

                      <p className="whitespace-pre-line mt-6">
                        {contentData?.seekHelp?.addendums?.[overallLevel]}
                      </p>

                      {/* Clears the float */}
                      <div className="clear-both" />
                    </div>
                  </div>
                </ExpandableSection>
              </section>

              {/* SECTION 7 - Resource */}
              <section className="rounded-xl shadow-lg overflow-hidden mb-8">
                <ExpandableSection
                  heroBanner={
                    <HeroBanner
                      title={contentData?.resources?.heading}
                      color="orange"
                    />
                  }
                >
                  <div className="flex flex-col gap-4">
                    <div className="grid grid-cols-1 lg:grid-cols-[50%_50%] gap-4 items-center">
                      <NextStepsCard
                        heading={contentData?.resources?.heading}
                        intro={contentData?.resources?.intro}
                        character={"i"}
                        bgClr="#eff4fd"
                        contentArray={contentData?.resources?.items}
                      />

                      <div className="flex justify-center items-center h-full">
                        <img
                          src={result5Image}
                          alt="Resources"
                          className="w-full max-w-full h-auto object-contain"
                        />
                      </div>
                    </div>

                    <DescriptiveCard
                      heading={contentData?.disclaimer?.heading}
                      overview={contentData?.disclaimer?.content}
                      bgClr="bg-orange-100"
                      textClr="text-yellow-800"
                    />
                  </div>
                </ExpandableSection>
              </section>

              {isAuth ? (
                <CallToActionSection
                  currentLocale={currentLocale}
                  t={t}
                  getAssessmentConnect={getAssessmentConnect}
                  getAssessmentAttemptByUserIdConnect={
                    getAssessmentAttemptByUserIdConnect
                  }
                  assessmentId={assessmentId}
                  userId={userGUID}
                  bgColor={ASSESSMENT_BACKGROUND_COLORS.BLUE_INDIGO}
                />
              ) : (
                CallToActionSectionForUnAuth({ contentData })
              )}

              {isAuth && (
                <InviteFriend
                  sectionMode={true}
                  getGuestUserConnect={getGuestUserConnect}
                  getOrderDataByUserIdConnect={getOrderDataByUserIdConnect}
                  getBillingConnect={getBillingConnect}
                  guestUserChangesConnect={guestUserChangesConnect}
                  isGuestOrPremiumUserConnect={isGuestOrPremiumUserConnect}
                  userId={userGUID}
                  userEmail={userData?.email}
                  buttonText={t("dashboard.invite_friend_cta")}
                  selectedPlan={selectedPlan}
                  assessmentData={currentAssessmentAttemptData}
                  showRemainingInvites={true}
                  bgColor={ASSESSMENT_BACKGROUND_COLORS.PURPLE_PINK}
                />
              )}
            </>
          )}
          <ReportWrapper
            assessmentId={assessmentId}
            assessmentAttemptId={assessmentAttemptId}
            currentLocale={currentLocale}
          />
        </div>
      </div>
      <Footer />
    </>
  );
};

export default SocialAnxietyReport;
